// Block 1 part E (47002): READ the customer position snapshot. The API stamps one figure set on every aged-receivables row of a customer
// (api lib/customerPosition.js: net_receivable, gross_receivable, owed_gross_by_age, credit_on_account, advances_held, refund_collectable_customer,
// unapplied_credit, collectable_cap ...). Nothing here derives a net, a gross or a cap: it picks a customer's figures once and, for an
// all-customers scope, adds the per-customer figures together.
import { poolKindsOf } from './onAccountPool.js';
const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const AGES = ['current', 'd31_60', 'd61_90', 'd90_plus'];

// One snapshot per customer (the first aged row carries it); optionally scoped to one partner id.
export function positionRows(agedRows, partnerId = null) {
  const seen = new Map();
  for (const r of (agedRows || [])) {
    if (partnerId && String(r.partner_id) !== String(partnerId)) continue;
    if (r.net_receivable == null || seen.has(r.partner_id)) continue;
    seen.set(r.partner_id, r);
  }
  return [...seen.values()];
}

export function receivablePosition(agedRows, partnerId = null) {
  const rows = positionRows(agedRows, partnerId);
  const sum = k => r2(rows.reduce((a, r) => a + (Number(r[k]) || 0), 0));
  const byAge = Object.fromEntries(AGES.map(b => [b, r2(rows.reduce((a, r) => a + (Number(r.owed_gross_by_age?.[b]) || 0), 0))]));
  return {
    rows, customers: rows.length, net: sum('net_receivable'), gross: sum('gross_receivable'), byAge,
    creditOnAccount: sum('credit_on_account'), advances: sum('advances_held'), refunds: sum('refund_collectable_customer'),
    refundsHeld: sum('refunds_held'), credit: sum('unapplied_credit'), collectable: sum('collectable_cap'),
  };
}

// The sign convention in words: > 0 the customer owes, < 0 the customer holds credit.
export const sideOf = net => (net > 0.005 ? 'owes' : net < -0.005 ? 'credit' : 'zero');

// The arithmetic behind the net, term by term, off the API snapshot (api lib/customerPosition.js: net = gross - credit_on_account - advances_held - refunds_held).
// `delta` is the 1-cent identity check; a non-zero delta is shown, never hidden.
export function reconOf(pos) {
  const { gross, creditOnAccount, advances, refundsHeld, net } = pos;
  return { gross, onAccount: creditOnAccount, advances, refunds: refundsHeld, net, delta: r2(gross - creditOnAccount - advances - refundsHeld - net) };
}

// ---- breakdown of the position terms (Gross owed / On account / Advances held / Refunds to pay): the SAME API snapshot, split, never re-derived ----------------
// One entry per customer plus the all-customers sum. `foots` is false when a split does not add back to its term (1-cent tolerance): shown as a flag, never hidden.
const c100 = n => Math.round((Number(n) || 0) * 100);
export function breakdownOf(row, partnerRows = []) {
  let orders = 0, orderLess = 0;
  for (const a of partnerRows) if (!a.account_role) for (const it of (a.items || [])) { const c = c100(it.open_amount ?? it.amount); if (c > 0) { if (it.partition === 'order_less') orderLess += c; else orders += c; } }
  const kinds = poolKindsOf(row), gross = r2(row.gross_receivable), onAcc = r2(row.credit_on_account);
  const age = Object.fromEntries(AGES.map(b => [b, r2(row.owed_gross_by_age?.[b])]));
  const overdue = r2(row.overdue_amount), dueSoon = r2(row.due_soon_amount);
  const held = r2(row.refunds_held), collectable = r2(row.refund_collectable_customer), net = r2(row.net_receivable);
  const sum = o => r2(Object.values(o || {}).reduce((a, v) => a + (Number(v) || 0), 0));
  const checks = {
    age: Math.abs(sum(age) - gross) < 0.011, split: Math.abs(r2(orders / 100 + orderLess / 100) - gross) < 0.011,
    due: overdue + dueSoon <= gross + 0.011, kinds: !kinds || Math.abs(sum(kinds) - onAcc) < 0.011,
    net: Math.abs(r2(gross - onAcc - r2(row.advances_held) - held - net)) < 0.011,
  };
  return {
    partner_id: row.partner_id, partner_name: row.partner_name, gross, age, overdue, dueSoon, later: r2(gross - overdue - dueSoon),
    orders: r2(orders / 100), orderLess: r2(orderLess / 100), onAccount: onAcc, kinds, advances: r2(row.advances_held),
    refundsHeld: held, collectable, blocked: r2(held - collectable), owes: net > 0.005, net,
    refundableCredit: r2(row.refundable_credit), checks, foots: Object.values(checks).every(Boolean),
  };
}
export function positionBreakdown(agedRows, partnerId = null) {
  const pos = receivablePosition(agedRows, partnerId);
  const by = new Map(); for (const a of (agedRows || [])) { const k = String(a.partner_id); if (!by.has(k)) by.set(k, []); by.get(k).push(a); }
  const customers = pos.rows.map(r => breakdownOf(r, by.get(String(r.partner_id)))).sort((a, b) => (b.gross + b.onAccount + b.advances + b.refundsHeld) - (a.gross + a.onAccount + a.advances + a.refundsHeld));
  const add = (k) => r2(customers.reduce((t, c) => t + c[k], 0));
  const kinds = customers.some(c => c.kinds) ? customers.reduce((o, c) => { for (const [k, v] of Object.entries(c.kinds || {})) o[k] = r2((o[k] || 0) + v); return o; }, {}) : null;
  const total = {
    gross: add('gross'), age: Object.fromEntries(AGES.map(b => [b, r2(customers.reduce((t, c) => t + c.age[b], 0))])), overdue: add('overdue'), dueSoon: add('dueSoon'), later: add('later'),
    orders: add('orders'), orderLess: add('orderLess'), onAccount: add('onAccount'), kinds, advances: add('advances'), refundsHeld: add('refundsHeld'), collectable: add('collectable'), blocked: add('blocked'),
  };
  return { customers, total, off: customers.filter(c => !c.foots).length };
}

// How the customers' net CREDIT splits and what can be paid out from where. Only customers in net credit contribute (credit = -net_receivable); the cash-refundable part is the API's
// refundable_credit (never recomputed here), the rest is advances held (refundable per order). Customers owing are summed apart, so credit - owing reconciles to the aggregate net.
// `foots` = credit == refundable + advances for every contributing customer (it does not when the credit is smaller than the advances held: flagged, not hidden).
export function creditSplit(rows) {
  const s = { credit: 0, refundable: 0, advances: 0, owing: 0, inCredit: 0, owingCount: 0, net: 0, off: 0 };
  for (const r of (rows || [])) {
    const net = Number(r.net_receivable) || 0; s.net += net;
    if (net < -0.005) {
      const credit = -net, ref = Number(r.refundable_credit) || 0, adv = Number(r.advances_held) || 0;
      s.credit += credit; s.refundable += ref; s.advances += adv; s.inCredit += 1;
      if (Math.abs(credit - ref - adv) > 0.011) s.off += 1;
    } else if (net > 0.005) { s.owing += net; s.owingCount += 1; }
  }
  for (const k of ['credit', 'refundable', 'advances', 'owing', 'net']) s[k] = r2(s[k]);
  return { ...s, foots: s.off === 0 };
}
